import * as lodash from 'lodash'
const _ = lodash.transform ? lodash : lodash.default
import { E, C, G } from './re'
import { lookupUnit } from '../util/quantifier'
import { LookupTableByName } from '../types/LookupTable'
import { UOMByName } from '../types/UnitOfMeasure'
import { Quantifier, UnitValue } from '../types/bindings'
import { StringKeyed } from '../types/util'
import { UnitOfMeasure } from '../types/dtos'

/**
 * RegEx-based Quantifier parser borne out through painstaking testing and tweaking.
 * Pottery Barn rules apply.
 */

const DEFAULT_OP = '='
const DECIMAL_NOTATION = '(?:[.\u00b7][0-9]+)|(?:[0-9][0-9,.\u00b7]*)'
const UNICODE_MULT = '\u00D7'
const MULT = `[${UNICODE_MULT}x*]`
const SCIENTIFIC_NOTATION = `${DECIMAL_NOTATION}${MULT}10\\^-?[1-9]+`
const SCIENTIFIC_NOTATION_RE = `(${DECIMAL_NOTATION})${MULT}10\\^(-?[1-9]+)`
export const PLUS_MINUS = '\xB1'
export const DEV_SEP = `(?:\\xB1|\\+/-|-/\\+|\\+-|-\\+)`

// const SD_RE = (capture = true) => `(?:\\s*\\xB1\\s*(${capture ? '' : '?:'}${DECIMAL_NOTATION}))`
// const NUMERIC = (capture = false) => `${capture ? '(' : '(?:'}(?:${SCIENTIFIC_NOTATION})|(?:${DECIMAL_NOTATION})${capture ? ')' : ')'}`
// const NUMERIC_WITH_SD = (capture = true) => `${NUMERIC(capture)}${SD_RE(capture)}?`
// const NUMERIC_OR_RATIO = (capture = true) => `(?:${capture ? '(' : '(?:'}${NUMERIC(false)}:${NUMERIC(false)}${capture ? ')' : ')'}|${NUMERIC_WITH_SD(capture)})`
// console.log('SD_RE', SD_RE(false), SD_RE(true))
// console.log('NUMERIC', NUMERIC(false))
// console.log('NUMERIC', NUMERIC(true))
// console.log('NUMERIC_WITH_SD', NUMERIC_WITH_SD(false), NUMERIC_WITH_SD(true))
// console.log('NUMERIC_OR_RATIO', NUMERIC_OR_RATIO(false), NUMERIC_OR_RATIO(true))
// const a = [//SD_RE(false), SD_RE(true), NUMERIC(false), NUMERIC(true), NUMERIC_WITH_SD(false), NUMERIC_WITH_SD(true), NUMERIC_OR_RATIO(false),
// 	NUMERIC_OR_RATIO(true)]



const SD_RE = (capture = true) => G(`\\s*${DEV_SEP}\\s*`, G('(?:\\s*-\\s*)?', `(?:${DECIMAL_NOTATION})`, capture))
const NUMERIC = G(`-?(?:${SCIENTIFIC_NOTATION})|-?(?:${DECIMAL_NOTATION})`)
const NUMERIC_WITH_SD = (capture = true) => G(NUMERIC.capture(capture) + SD_RE(capture) + '?')
//`(?:(?:${NUMERIC}:${NUMERIC})|${NUMERIC_WITH_SD(capture)})` // NUMERIC + '(?::' + NUMERIC + ')?'
const RATIO = G(`${NUMERIC}:${NUMERIC}`)
const NUMERIC_OR_RATIO = (capture = true) => G([RATIO.capture(capture), NUMERIC_WITH_SD(capture)])


// capture the operator: (>=|>|=|<|<=)
const createOpRe = (ops) => {
	return ops.map(_.escapeRegExp).join('|')
}

// capture the (possibly negative) numeric value
const VALUE_RE = (capture = true) => NUMERIC_OR_RATIO(capture)

const RANGE_SPEC_RE = '(?:-|to)'

const OP_AND_VALUE = (opRe, capture: boolean, requireOp: boolean, requireValue: boolean) => {
	const c = capture ? '' : '?:'
	return `(${c}${opRe})${requireOp ? '' : '?'}\\s*${VALUE_RE(capture)}${requireValue ? '' : '?'}`
}

const POSSIBLE_TRAILING_OP_AND_VALUE = opRe => OP_AND_VALUE(opRe, false, false, false)
const TRAILING_OP_AND_VALUE = opRe => OP_AND_VALUE(opRe, false, false, true)

const UNIT_VALUE_SEP = '\\s*\\/\\s*' // should we limit the amount of space accepted around slashes when scanning arbitrary text?
const UNIT_VALUE_SEP_RE = (opRe, requireTrailingValue) =>
	`(?:${UNIT_VALUE_SEP}${requireTrailingValue ? TRAILING_OP_AND_VALUE(opRe) : POSSIBLE_TRAILING_OP_AND_VALUE(opRe)})`

// capture the unit (without capturing a following range specifier)
const UNIT_RE = (opRe, exact = true, capture = false, requireTrailingValue = true, anyTrailingIsUnit = true, until) => {
	const c = capture ? '' : '?:'
	const separator = UNIT_VALUE_SEP_RE(opRe, requireTrailingValue)
	until = until ? `(?:${separator}|${until})` : separator
	// if we are parsing a piece of text we know is *exactly* a quantifier,
	// we can accept any trailing characters as the unit
	// otherwise we should stop at whitespace
	// if we are parsing an exact quantifier then we can allow spaces within units
	const REASONABLE_UNIT_CHARS = '[^;,().\\\\' + (exact ? '' : '\\s') + ']' // XXX: ANYTHING = '.'
	const char = anyTrailingIsUnit ?  REASONABLE_UNIT_CHARS : '\\S'
	//return `(${c}(?:(?!${until})${char})+)?`
	return `\\s*(${c}(?:(?:N\\/A(?=\\s*(?:\\/|$)))|(?:(?!${until})${char})+))?\\s*`
}

const UNIT_VALUE_RE = (opRe, exact: boolean, capture: boolean, requireOp: boolean, requireValue: boolean, requireNextHasValue: boolean, anyTrailingIsUnit: boolean, until) => {
	const measure = OP_AND_VALUE(opRe, capture, requireOp, requireValue)
	return measure + '\\s*' + UNIT_RE(opRe, exact, capture, requireNextHasValue, anyTrailingIsUnit, until)
}

const LEADING_UNIT_VALUE_RE = (opRe, exact: boolean, capture: boolean, requireOp: boolean, requireNextHasValue: boolean, anyTrailingIsUnit: boolean, until) =>
	UNIT_VALUE_RE(opRe, exact, capture, requireOp, true /* leading unit values require a value*/, requireNextHasValue, anyTrailingIsUnit, until)

const TRAILING_UNIT_VALUE_RE = (opRe, exact: boolean, capture: boolean, anyTrailingIsUnit: boolean, until) =>
	/* subsequent unit values do not require an op or a value */
	UNIT_VALUE_RE(opRe, exact, capture, false, false, false, anyTrailingIsUnit, until)

const DETECT_UNIT_VALUES_RE = (opRe, exact: boolean, requireLeadingOp: boolean, anyTrailingIsUnit: boolean, until) => {
	/* XXX */ requireLeadingOp = false
	const leading = LEADING_UNIT_VALUE_RE(opRe, exact, false, requireLeadingOp, true /* doesn't really matter for detection, we're not capturing */, anyTrailingIsUnit, until)
	// don't need to use trailing value RE, we really just want to identify and consume as quickly as possible as we are only detecting not capturing
	const rest = requireLeadingOp ? LEADING_UNIT_VALUE_RE(opRe, exact, false, false, true, anyTrailingIsUnit, until) : leading
	return `(?:${leading})(?:${UNIT_VALUE_SEP}${rest})*`
}

const COMPOUND_UNIT_VALUES_RE = (opRe) => {
	const leading = LEADING_UNIT_VALUE_RE(opRe, true, true,
		false /* we've already detected the entire bound, so don't need to impose requirement on presence of op*/,
		true	/* we DO want to try to force consumption of the entire compound unit if no value is present */,
		true, undefined)
	return `^${leading}$`
}

const LOW_BOUND = (opRe, exact = true) => {
	const anyTrailingCharsIsUnit = true
	const rangeWithOptionalOp = `(?:${RANGE_SPEC_RE}\\s?${LEADING_UNIT_VALUE_RE(opRe, false, false, false, false, anyTrailingCharsIsUnit, undefined)})`
	const optionalRangeWithOp = `(?:${RANGE_SPEC_RE}?\\s?${LEADING_UNIT_VALUE_RE(opRe, false, false, true, false, anyTrailingCharsIsUnit, undefined)})`
	const nextBound = `${rangeWithOptionalOp}|${optionalRangeWithOp}`
	// require leading operator if we are scanning arbitrary text
	return DETECT_UNIT_VALUES_RE(opRe, exact, !exact, anyTrailingCharsIsUnit , nextBound)
}

const HIGH_BOUND = (opRe, exact = true) =>
	DETECT_UNIT_VALUES_RE(opRe, exact, false, exact, undefined)

const QUANTIFIER_REGEX = (opRe, exact = true) => {
	const lowBound = LOW_BOUND(opRe, exact)
	// on the high bound, if we are parsing quantifiers in inexact string of text
	// then do not accept *any* trailing chars as unit, but instead be more cautious
	// and break on whitespace
	const highBound = HIGH_BOUND(opRe, exact)
	const pattern = '\\s*(' + lowBound + ')(?:\\s*' + RANGE_SPEC_RE + '\\s*(' + highBound + '))?'
	return exact ? `^${pattern}$` : pattern
}

const UNIT_FIXES = {
	Week: 'wk',
	Weeks: 'wk',
	week: 'wk',
	weeks: 'wk',
	w: 'wk',
	wks: 'wk',
	month: 'mo',
	months: 'mo',
	days: 'd',
	day: 'd',
	year: 'yr',
	years: 'yr',
	yrs: 'yr',
	second: 's',
	minute: 'm',
	hour: 'hr',
	hours: 'hr',
	hrs: 'hr',
	microgram: 'μg',
	micrograms: 'μg',
	gram: 'g'
}

function toUnitValues(op?: string, val = '1', sdstr?: string, uom?: UnitOfMeasure): UnitValue[] {
	const numericParts = val.split(':')
	const unitValues = []
	const sd = Number(sdstr)
	for (const numericPart of numericParts) {
		let num = Number(numericPart)
		if (_.isNaN(num)) {
			const sn_re = new RegExp(SCIENTIFIC_NOTATION_RE)
			let result
			if (result = sn_re.exec(val)) {
				num = Number(`${result[1]}e${result[2]}`)
			} else {
				//@ts-ignore
				num = val
			}
		}
		const unitValue: UnitValue = {
			EqualityOperator: op,
			Multiplier: 1,
			Number: num,
		}
		if (sd != undefined && !Number.isNaN(sd)) {
			unitValue.SD = sd
		}
		Object.defineProperty(unitValue, 'num_str', { value: numericPart })
		Object.defineProperty(unitValue, 'sd_str', { value: sdstr })
		if (uom != null) {
			unitValue.Unit = uom
		}
		unitValues.push(unitValue)
	}
	return unitValues
}

export interface ParseResult {
	quantifier: Quantifier,
	offset: number,
	length: number
}

class QuantifierParser {
	private readonly strictQuantifierRE: RegExp
	private readonly scanQuantifierRE: RegExp
	private readonly leadingUnitValueRE: RegExp
	private readonly trailingUnitValueRE: RegExp
	private readonly compoundUnitValuesRE: RegExp
	readonly naUnit: UnitOfMeasure

	constructor(readonly eqOps: LookupTableByName, readonly uoms: UOMByName = {}, private readonly aliases?: StringKeyed<string>) {
		const eqOpsKeys = Object.keys(eqOps)
		if (eqOpsKeys.length == 0) {
			console.warn('QuantifierParser initialized without any equality operators')
		}
		this.aliases = aliases ? Object.assign({}, UNIT_FIXES, aliases) : UNIT_FIXES
		// find the default "Not Applicable" unit
		const naUnit = this.lookupUnit('na') || this.lookupUnit('n/a')
		this.naUnit = naUnit || {
			Name: {
				Name: 'NA'
			}
		}

		const opRe = createOpRe(eqOpsKeys)
		// "strict" quantifier parsing and "scanning" text differ in that:
		// * when scanning arbitrary text, leading operator is required to indicate quantifier start
		// * when scanning arbitrary text multiple matches are retrieved
		// * strict quantifier parsing enforces matching at beginning and end of input (single match)
		// * strict quantifier parsing allows arbitrary trailing unit of measure text, scanning stops at space
		//	 (so as not to keep accumulating garbage onto the unit name)
		this.strictQuantifierRE = new RegExp(QUANTIFIER_REGEX(opRe, true), 'i')
		this.scanQuantifierRE = new RegExp(QUANTIFIER_REGEX(opRe, false), 'ig')
		this.leadingUnitValueRE = new RegExp(LEADING_UNIT_VALUE_RE(opRe, true, true, false, false, true, undefined), 'ig')
		this.trailingUnitValueRE = new RegExp(TRAILING_UNIT_VALUE_RE(opRe, true, true, true, undefined), 'ig')
		this.compoundUnitValuesRE = new RegExp(COMPOUND_UNIT_VALUES_RE(opRe))
	}

	lookupUnit(str, uoms = this.uoms, aliases = this.aliases) {
		if (str) str = str.trim()
		return lookupUnit(aliases[str] || str, uoms)
	}

	parse(text): Quantifier {
		const results = this.parseText(text, true)
		if (results.length > 0) {
			return results[0].quantifier
		}
	}

	parseText(text, exact = false): ParseResult[] {
		const qre = exact ? this.strictQuantifierRE : this.scanQuantifierRE
		// reset reused RE
		qre.lastIndex = 0
		const results = []
		let result
		while (result = qre.exec(text)) {
			let offset = result.index
			let length = result[0].length
			const trimStart = result[0].trimStart()
			const adjust = result[0].length - trimStart.length
			offset += adjust
			length -= adjust
			const trimEnd = trimStart.trimEnd()
			length -= trimStart.length - trimEnd.length
			const quantifier: Quantifier = {
				Gestalt: trimEnd,
				High: undefined
			}
			const lowText = result[1]
			const lowUnitValues = this.parseUnitValues(lowText)
			const highText = result[2]
			let highUnitValues
			if (highText) {
				highUnitValues = this.parseUnitValues(highText, DEFAULT_OP)
				this.mergeUnitValues(lowUnitValues, highUnitValues)
				this.defaultUnitValues(lowUnitValues)
				this.defaultUnitValues(highUnitValues)
				quantifier.Low = { Units: lowUnitValues}
				quantifier.High = { Units: highUnitValues }
			} else {
				this.defaultUnitValues(lowUnitValues)
				quantifier.High = { Units: lowUnitValues }
			}
			results.push({
				quantifier,
				offset,
				length
			})
			if (exact) break
		}
		return results
	}

	// handle special case of missing lower units and compound upper unit
	unifyLowerBound(lowUnitValues, highUnitValues) {
		const lowIsMissing = lowUnitValues.length && !lowUnitValues[0]?.Unit
		//const lowIsSingleCompound = highUnitValues.every((uv, i) => uv.Unit && (i == 0 || uv.Number == 1))
		//const highIsMissing = lowUnitValues[0] && !lowUnitValues[0]?.Unit
		const highIsSingleCompound = highUnitValues.length && highUnitValues.every((uv, i) => uv.Unit && (i == 0 || (uv.EqualityOperator == '=' && uv.Number == 1)))
		if (lowIsMissing && highIsSingleCompound) {
			lowUnitValues[0].Unit = highUnitValues[0].Unit
			lowUnitValues.push(...highUnitValues.slice(1).map(uv => {
				const copied = _.clone(uv)
				Object.defineProperty(copied, 'num_str', { value: uv.num_str })
				Object.defineProperty(copied, 'sd_str', { value: uv.sd_str })
				return copied
			}))
			return true
		}
	}

	mergeUnitValues(lowUnitValues, highUnitValues) {
		//if (this.unifyLowerBound(lowUnitValues, highUnitValues)) return
		if (lowUnitValues.length != highUnitValues.length) return
		const units = []
		for (let i = 0; i < lowUnitValues.length; i++) {
			const low = lowUnitValues[i].Unit
			const high = highUnitValues[i].Unit
			if (!low && !high) continue
			if (low && !high) {
				units[i] = low
			} else if (!low && high) {
				units[i] = high
			} else {
				if (low.Name.Name != high.Name.Name) {
					// we have a unit conflict, abort backfilling
					return
				}
			}
		}
		for (let i = 0; i < units.length; i++) {
			const unit = units[i]
			if (unit) {
				const low = lowUnitValues[i]
				const high = highUnitValues[i]
				if (low.Unit != unit) low.Unit = _.clone(unit)
				if (high.Unit != unit) high.Unit = _.clone(unit)
			}
		}
	}

	defaultUnitValues(unitValues) {
		for (const unit of unitValues) {
			if (!unit.Unit) {
				unit.Unit = this.naUnit
			}
		}
	}

	parseUnitValues(bound, lowOp = DEFAULT_OP): UnitValue[] {
		// reset reused RE
		this.leadingUnitValueRE.lastIndex = this.trailingUnitValueRE.lastIndex = this.compoundUnitValuesRE.lastIndex = 0

		const unitValues = []
		// first detect if this is a single compound unit
		let result = this.compoundUnitValuesRE.exec(bound)
		if (result) {
			const ratio = result[2]
			const numeric = result[3]
			const sd = result[4]
			const unit = result[5]
			const uom = unit ? this.lookupUnit(unit) : undefined
			if (uom) {
				// we have a single compound unit which matches an existing definition, use it
				const opStr = result[1]
				const op = this.eqOps[opStr]?.Name || opStr
				const val = ratio || numeric
				unitValues.push(...toUnitValues(op, val, sd, uom))
			}
		}
		if (unitValues.length == 0) {
			// parse individual unit values
			let i = 0
			result = this.leadingUnitValueRE.exec(bound)
			this.trailingUnitValueRE.lastIndex = this.leadingUnitValueRE.lastIndex + 1
			while (result != null) {
				if (i == 10) {
					throw new Error()
				}
				i++
				const opStr = result[1]
				const op = this.eqOps[opStr]?.Name || opStr
				const ratio = result[2]
				const numeric = result[3]
				const sd = result[4]
				const unit = result[5]
				// ratio may result in 2 unit values
				const val = ratio || numeric
				unitValues.push(...toUnitValues(op, val, sd, unit && (this.lookupUnit(unit) ?? { Name: { Name: unit.trim() } })))
				result = this.trailingUnitValueRE.lastIndex < bound.length ? this.trailingUnitValueRE.exec(bound) : undefined
				while (result && result[0].length == 0) {
					this.trailingUnitValueRE.lastIndex++
					result = this.trailingUnitValueRE.lastIndex < bound.length ? this.trailingUnitValueRE.exec(bound) : undefined
				}
			}
		}
		// assign default ops if missing
		for (const unitValue of unitValues) {
			if (!unitValue.EqualityOperator) {
				unitValue.EqualityOperator = unitValues.length == 1 ? lowOp : DEFAULT_OP
			}
		}
		return unitValues
	}
}

export function hasValidUnits(quantifier: Quantifier) {
	if (!quantifier.Low && !quantifier.High) return false
	for (const pole of [quantifier.Low, quantifier.High]) {
		if (pole && pole.Units && pole.Units.length > 0) {
			if (!pole.Units.every(unitValue => unitValue.Unit && unitValue.Unit['Id'] != null)) return false
		}
	}
	return true
}

let parser: QuantifierParser = null

export default {
	get parser() {
		return parser
	},
	init(eqOps: LookupTableByName, uoms: UOMByName, aliases?) {
		return parser || this.reinit(eqOps, uoms, aliases)
	},
	reinit(eqOps: LookupTableByName, uoms: UOMByName, aliases?) {
		return parser = new QuantifierParser(eqOps, uoms, aliases)
	},
	hasValidUnits,
	lookupUnit: (str, uoms?, aliases?) => parser.lookupUnit(str, uoms, aliases),
	parse(text) { return parser.parse(text) },
	parseText(text, exact) { return parser.parseText(text, exact) }
}